import React, { useEffect, useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, useAutocomplete } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BLACK, GREY, SCOUT_TEAL, WHITE } from '../../../ThemeContext/constants';

import type { AutocompleteGetItemProps } from '@mui/material';
import type { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';

export interface IMultiSelectProps {
  dropdownOptions: IOptionType[];
  onChange: (newValues: (string | IOptionType)[]) => void;
  values: IOptionType[];
  loadingOptions: boolean;
  creatingOption: boolean;
  hasMaximumCharacter?: number;
  defaultEmptyLabel?: string;
  createDisabled?: boolean;
  onInputChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
  filterOptionsLocally?: boolean;
  // Lets a caller force-clear the visible search text (e.g. after removing a
  // chip) without disabling/blurring the input. Bump to a new value to reset.
  clearInputSignal?: number;
}

export interface IOptionType {
  id: number;
  label?: string;
  title?: string;
  name?: string;
}

interface TagProps extends Omit<ReturnType<AutocompleteGetItemProps<true>>, 'key'> {
  label: string;
}

const Tag = (props: TagProps) => {
  const { label, onDelete, ...other } = props;
  return (
    <div {...other}>
      <span>{label}</span>
      <CloseIcon onClick={onDelete} />
    </div>
  );
};

const InputWrapper = styled('div')(
  () => `
  width: 100%;
  min-height: 41px;
  border: 1px solid #DADEE0;
  background-color: ${WHITE};
  border-radius: 6px;
  padding: 6px 22px 6px 6px;
  display: flex;
  flex-wrap: wrap;
  margin-top: 3px;
  &:hover {
    border-color: #DADEE0;
  }

  &.focused {
    border-color: #77C2BE;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  & input {
    background-color: transparent;
    color: ${BLACK};
    box-sizing: border-box;
    padding: 4px 6px 2px 6px;
    width: 0;
    height: 26px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
    z-index: 1;
  }

  & .select-placeholder {
    font-size: 15px;
    font-weight: 500;
    color: ${GREY};
    margin-left: 8px;
    margin-top: 2px;
    position: absolute;
    z-index: 0;
  }

  & .select-arrow {
    position: absolute;
    right: 4px;
    bottom: 0px;
    height: 100%;
    fontSize: 1.1rem;
    color: #AAAAAA;
    transition: transform 0.25s ease-in-out;
  }
`
);

const StyledTag = styled(Tag)<TagProps>(
  () => `
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  height: 23px;
  margin: 2px;
  line-height: 22px;
  background-color: #F3F3F3;
  border: none;
  border-radius: 16px;
  box-sizing: content-box;
  padding: 0 4px 0 10px;
  outline: 0;
  overflow: hidden;

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & svg {
    font-size: 9px;
    cursor: pointer;
    margin: 0px 4px 0px 6px;
  }
`
);

const Listbox = styled('ul')(
  () => `
  width: 100%;
  margin: 4px 0 0;
  padding: 0;
  position: absolute;
  list-style: none;
  background-color: ${WHITE};
  overflow: auto;
  overflow-x: hidden;
  max-height: 160px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 2;

  & .Mui-focused {
    background: #F1F1F1 !important;
  }

  & li {
    padding: 5px 12px;
    display: flex;
    font-weight: 600;
    user-select: none;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -o-user-select: none;
    cursor: pointer;

    & span {
      flex-grow: 1;
    }

    & svg {
      color: transparent;
    }

  }

  & li[aria-selected='true'] {
    background-color: #FAFAFA;
    font-weight: 600;

    & svg {
      color: ${SCOUT_TEAL};
    }
  }

  & li[data-focus='true'] {
    background-color: #e6f7ff;
    cursor: pointer;  

    & svg {
      color: currentColor;
    }
  }
`
);

const MultiSelectWithCreate = ({
  dropdownOptions,
  onChange,
  values,
  loadingOptions,
  creatingOption,
  defaultEmptyLabel,
  createDisabled,
  onInputChange,
  filterOptionsLocally = true,
  clearInputSignal
}: IMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // A caller bumps clearInputSignal (e.g. after removing a chip) to force the
  // visible search text back to empty, without disabling/blurring the input
  // the way toggling `disabled` on `useAutocomplete` does. Own state
  // (inputValue) is adjusted during render; notifying the parent via
  // onInputChange is a side effect on another component, so it happens in an
  // effect instead of react-dom's set-state-in-render warning.
  const isFirstRender = useRef(true);
  const [lastClearInputSignal, setLastClearInputSignal] = useState(clearInputSignal);
  if (clearInputSignal !== lastClearInputSignal) {
    setLastClearInputSignal(clearInputSignal);
    if (inputValue !== '') setInputValue('');
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (clearInputSignal === undefined) return;
    onInputChange?.('', 'clear');
    // Only re-run when the signal itself changes, not on every inputValue/onInputChange identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearInputSignal]);

  const {
    getRootProps,
    getInputProps,
    getItemProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value,
    focused,
    setAnchorEl,
    anchorEl
  } = useAutocomplete({
    open,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    inputValue,
    onInputChange: (_event, newValue, reason) => {
      setInputValue(newValue);
      onInputChange?.(newValue, reason);
    },
    id: 'multi-select',
    multiple: true,
    options: dropdownOptions || [],
    getOptionLabel: (option: IOptionType | string) =>
      typeof option === 'string' ? option : (option.name ?? option.label ?? option.title ?? ''),
    value: values,
    onChange: (_event, newValues) => onChange(newValues),
    freeSolo: true,
    autoHighlight: true,
    disabled: creatingOption,
    filterOptions: filterOptionsLocally ? undefined : (options) => options
  });
  const handleCreateFromEmptyState = () => {
    if (!creatingOption && inputValue.trim()) {
      onChange([...value, inputValue.trim()]);
      setInputValue('');
      setOpen(false);
    }
  };

  let emptyStateLabel = defaultEmptyLabel ?? 'No options found';
  if (!creatingOption && !createDisabled && inputValue.trim()) {
    emptyStateLabel = `Create "${inputValue.trim()}"`;
  }

  const getOptions = () => {
    if (groupedOptions.length > 0) {
      return (
        <Listbox {...getListboxProps()} sx={{ maxWidth: anchorEl?.getBoundingClientRect().width }}>
          {groupedOptions.map((option, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li {...getOptionProps({ option, index })} key={index}>
              <span>{option.name ?? option.label ?? option.title ?? ''}</span>
              <CheckIcon fontSize="small" />
            </li>
          ))}
        </Listbox>
      );
    }

    if (!open && (!inputValue.trim() || createDisabled)) {
      return null;
    }

    return (
      <Listbox sx={{ maxWidth: anchorEl?.getBoundingClientRect().width }}>
        <li style={{ width: '100%' }}>
          <button
            disabled={!inputValue.trim() || creatingOption || createDisabled}
            onClick={handleCreateFromEmptyState}
            type="button"
            style={{
              all: 'unset',
              display: 'block',
              width: '100%'
            }}
          >
            {emptyStateLabel}
          </button>
        </li>
      </Listbox>
    );
  };

  return (
    <>
      <div {...getRootProps()}>
        <InputWrapper
          ref={setAnchorEl}
          className={focused ? 'focused' : ''}
          sx={{ position: 'relative' }}
        >
          {value.length < 1 && inputValue.length < 1 && !focused && (
            <span className="select-placeholder">
              {loadingOptions ? 'Loading options...' : 'Select or create one or more'}
            </span>
          )}
          {creatingOption ? (
            <CircularProgress size={24} sx={{ position: 'absolute', right: '14px', top: '8px' }} />
          ) : (
            <ArrowDropDownIcon
              className="select-arrow"
              onClick={() => setOpen(!open)}
              sx={
                open
                  ? { transform: 'rotate(-180deg)', '&:hover': { cursor: 'pointer' } }
                  : { '&:hover': { cursor: 'pointer' } }
              }
            />
          )}
          {value.map((option: IOptionType | string, index: number) => {
            if (typeof option === 'string') {
              // eslint-disable-next-line react/jsx-key
              return <StyledTag label={option} {...getItemProps({ index })} />;
            }
            return (
              // eslint-disable-next-line react/jsx-key
              <StyledTag
                label={option.name ?? option.label ?? option.title ?? ''}
                {...getItemProps({ index })}
              />
            );
          })}
          <input {...getInputProps()} />
        </InputWrapper>
      </div>
      {getOptions()}
    </>
  );
};

export default MultiSelectWithCreate;
